import * as React from 'react';

import {CoreEntityConfirmationDialog} from 'shells/core-entity-confirmation-dialog/core-entity-confirmation-dialog';
import type {CoreEntityConfirmationType} from 'shells/core-entity-confirmation-dialog/types';
import {ArchiveRegularIcon} from 'shells/icon/svg/archive-regular-icon';
import {CalendarSquareRegularActivityIcon} from 'shells/icon/svg/calendar-square-regular-activity-icon';
import {PersonWarningIcon} from 'shells/icon/svg/person-warning-icon';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {AccountCard} from 'shells/peep-cards/account-card/account-card';
import type {BaseRowStatusBadge} from 'shells/row/base-row/base-row';
import type {ToolbarOption} from 'shells/toolbar/toolbar';

import type {GetAccountsForLead_lead_accounts_edges_node_phones_primary_value as PhoneValue} from 'nutshell-graphql-types';

import {appHistory} from '../../../../history';

import {ConnectButtonsRow} from './connect-buttons-row';
import {getUserEntityFromApiType} from './helpers';
import {HoverableEntityAvatar} from './hoverable-entity-avatar';
import type {ApiEntity, HoverviewOrientation} from './types';

export interface Props {
    accountId: string;
    name: string | null | undefined;
    accountUrl: string | null | undefined;
    description: string | null | undefined;
    location: string | null | undefined;
    avatarUrl: string | null | undefined;
    role?: string | null | undefined;
    parentEntityName: ApiEntity;
    isDuplicate: boolean | null | undefined;
    isFormer?: boolean | null | undefined;
    removeAccount: (arg1: string) => void;
    setError?: (arg1: string) => void;
    phoneCallerType: string | null | undefined;
    phone: PhoneValue | null | undefined;
    hasConnectedPhoneIntegration: boolean;
    prependToolbarOptions?: ToolbarOption[];
    prependStatusBadges?: BaseRowStatusBadge[];
    isParent?: boolean;
    canRemove?: boolean;
    deleteType?: Extract<CoreEntityConfirmationType, 'company' | 'association'>;
    orientationOverride?: HoverviewOrientation;
    successFlashKey?: string;
}

export function CompanyRow(props: Props) {
    const {removeAccount, canRemove = true, deleteType = 'company'} = props;

    const [isHovered, setIsHovered] = React.useState<boolean>(false);
    const [forceToolbarOpen, setForceToolbarOpen] = React.useState<boolean>(false);
    const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] = React.useState<boolean>(false);

    const duplicateRef = React.useRef<HTMLElement | null | undefined>(null);
    const removeButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const handleDuplicateClick = () => {
        if (duplicateRef.current) {
            const $element = $(duplicateRef.current);
            setForceToolbarOpen(true);

            const handlePopoverClose = () => {
                setForceToolbarOpen(false);
                $element.off('popoverWasClosed', handlePopoverClose);
            };

            $element.on('popoverWasClosed', handlePopoverClose);

            Popover.load({
                url: '/de-duplication/popover.json',
                data: {
                    entityName: 'Contacts',
                    entityId: props.accountId,
                    parentEntityName: props.parentEntityName,
                    orientationOverride: props.orientationOverride,
                },
                appendTo: $element,
                noLoadingIndicator: true,

                error: function () {
                    setForceToolbarOpen(false);
                    $element.off('popoverWasClosed', handlePopoverClose);
                    props.setError?.('Failed to load duplicate popover');
                },
            });
        }
    };

    const handleMouseEnter = React.useCallback(() => {
        if (!isHovered) {
            setIsHovered(true);
        }
    }, [isHovered]);

    const handleMouseOut = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as any)) {
            return;
        }

        setIsHovered(false);
    }, []);

    const handleRemoveAccount = React.useCallback(() => {
        setIsRemoveConfirmationOpen(false);
        setForceToolbarOpen(false);
        removeAccount(props.accountId);
    }, [props.accountId, removeAccount]);

    const handleCancelRemoveAccount = React.useCallback(() => {
        setIsRemoveConfirmationOpen(false);
        setForceToolbarOpen(false);
    }, []);

    const parentEntity = getUserEntityFromApiType(props.parentEntityName);

    const toolbarOptions: ToolbarOption[] = [
        ...(props.prependToolbarOptions || []),
        {
            id: 'company-schedule-activity',
            tooltipText: 'Schedule activity',
            icon: CalendarSquareRegularActivityIcon,
            onClick: () => {
                appHistory.replace({
                    ...appHistory.location,
                    hash: `/create/activities/${props.accountId}`,
                    state: {modal: true},
                });
            },
        },
    ];

    if (canRemove) {
        toolbarOptions.push({
            id: 'company-remove-from-lead',
            tooltipText: `Remove ${
                deleteType === 'association' ? 'association' : ''
            } from ${parentEntity}`,
            icon: TrashRegularIcon,
            onClick: () => {
                setForceToolbarOpen(true);
                setIsRemoveConfirmationOpen(true);
            },
            getRef: (ref) => {
                removeButtonRef.current = ref;
            },
        });
    }

    const statusBadges: BaseRowStatusBadge[] = [...(props.prependStatusBadges || [])];
    if (props.isDuplicate) {
        statusBadges.unshift({
            text: 'Duplicate?',
            level: 'alert',
            icon: PersonWarningIcon,
            onClick: handleDuplicateClick,
            getRef: (ref) => {
                duplicateRef.current = ref;
            },
        });
    }
    if (props.isFormer) {
        statusBadges.push({
            text: 'Former company',
            level: 'info--grey',
            icon: ArchiveRegularIcon,
        });
    }

    return (
        <>
            <div onMouseEnter={handleMouseEnter} onMouseOut={handleMouseOut}>
                <AccountCard
                    name={props.name}
                    icon={
                        <HoverableEntityAvatar
                            avatarUrl={props.avatarUrl}
                            entityId={props.accountId}
                            entityName='Accounts'
                            parentEntityName={props.parentEntityName}
                            isHovered={isHovered}
                            isParent={props.isParent}
                            orientationOverride={props.orientationOverride}
                        />
                    }
                    accountUrl={props.accountUrl}
                    description={props.description}
                    location={props.location}
                    role={props.role}
                    toolbarProps={{options: toolbarOptions, forceShowToolbar: forceToolbarOpen}}
                    statusBadges={statusBadges}
                    phoneNumber={props.phone && props.phone.numberFormattedWithCountryCode}
                    phoneExtension={props.phone && props.phone.extension}
                    connectButtonsRow={
                        <ConnectButtonsRow
                            id={props.accountId}
                            name={props.name}
                            phone={props.phone}
                            phoneCallerType={props.phoneCallerType}
                            hasConnectedPhoneIntegration={props.hasConnectedPhoneIntegration}
                        />
                    }
                    successFlashKey={props.successFlashKey}
                />
            </div>
            {isRemoveConfirmationOpen && (
                <CoreEntityConfirmationDialog
                    anchor={removeButtonRef.current}
                    type={deleteType}
                    parentEntity={parentEntity}
                    onConfirm={handleRemoveAccount}
                    onCancel={handleCancelRemoveAccount}
                />
            )}
        </>
    );
}
